// hooks/use-forgot-password.ts
import { useFormik } from "formik";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useSendOtp } from '../../../apis/auth/sendOtp';
import { useVerifyOtp } from '../../../apis/auth/verifyOtp';
import { useResetPassword } from '../../../apis/auth/resetPassword';
import { step1ValidationSchema, step2ValidationSchema, step3ValidationSchema } from '../../../lib/validations/login-validation';

const useForgotPassword = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [isResendDisabled, setIsResendDisabled] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [resendAttempts, setResendAttempts] = useState(0);
    const [token, setToken] = useState("");

    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const step1Form = useFormik({
        initialValues: {
            email: searchParams.get("email") ?? "",
        },
        validationSchema: step1ValidationSchema,
        onSubmit: async (values) => {
            const res = await sendOtp();
            if (res?.isSuccess) {
                toast.success(res.data?.message);
                setCurrentStep(2);
                setIsResendDisabled(true);
                setResendAttempts(0);
                startCountdown(60);
            } else if (res?.isError) {
                toast.error((res.error as any)?.response?.data?.error || "Something went wrong, please try again later.");
            }
        },
    });

    const { refetch: sendOtp } = useSendOtp(step1Form.values.email);
    const { mutateAsync: verifyOtp } = useVerifyOtp();
    const { mutateAsync: resetPassword } = useResetPassword();

    const startCountdown = useCallback((duration: number) => {
        setCountdown(duration);
        setIsResendDisabled(true);

        if (timerRef.current) clearInterval(timerRef.current);

        timerRef.current = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timerRef.current!);
                    setIsResendDisabled(false);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, []);

    useEffect(() => {
        if (currentStep === 2) startCountdown(60);
    }, [currentStep, startCountdown]);

    const formatCountdown = (seconds: number) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
    };

    const step2Form = useFormik({
        initialValues: {
            otp: "",
        },
        validationSchema: step2ValidationSchema,
        onSubmit: async (values) => {
            try {
                const res = await verifyOtp({
                    code: parseInt(values.otp),
                    email: step1Form.values.email,
                });
                toast.success(res?.message);
                setToken(res?.token);
                setCurrentStep(3);
            } catch (error) {
                toast.error("Invalid OTP");
            }
        },
    });

    const step3Form = useFormik({
        initialValues: {
            password: "",
        },
        validationSchema: step3ValidationSchema,
        onSubmit: async (values) => {
            try {
                const res = await resetPassword({ password: values.password, token });
                toast.success(res?.message);
                navigate("/login");
            } catch (error) {
                toast.error("Something went wrong, please try again later.");
            }
        },
    });

    const handleResendOtp = async () => {
        if (isResendDisabled) return;
        const newAttempts = resendAttempts + 1;
        setResendAttempts(newAttempts);

        const res = await sendOtp();
        if (res?.data) {
            toast.success(res.data?.message);
            setIsResendDisabled(true);
        } else {
            toast.error("Email not found");
        }

        if (newAttempts === 1) {
            startCountdown(300);
        } else if (newAttempts >= 2) {
            setIsResendDisabled(true);
        }
    };

    return {
        currentStep,
        isResendDisabled,
        countdown: formatCountdown(countdown),
        resendAttempts,
        step1Form,
        step2Form,
        step3Form,
        handleResendOtp,
    };
};

export default useForgotPassword;